import React, { useEffect, useState } from "react";

import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  message,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Upload,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import { useHistory } from "react-router-dom";

/* eslint-disable react/prop-types */
import {
  DownloadOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import RefreshEarningsButton from "../../Components/RefreshEarningsButton/RefreshEarningsButton";
import useDebounce from "../../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchManagers } from "../../store/Thunks/appThunk";
import {
  getCohortSheet,
  getCreatorsEarnings,
  getCreatorWalletStatement,
  uploadBulkAccountManager,
  validateBulkAccountManager,
} from "../api";
import WalletLedgerTable from "../WalletWithdrawals/WalletLedgerTable";
import CreatorNatureAction from "./CreatorNatureAction";
import MangoExpandTable from "./MangoExpandTable";

const CreatorEarnings = () => {
  const dispatch = useAppDispatch();
  const { managerState } = useAppSelector((state) => state.app);
  const { managers } = managerState;
  const history = useHistory();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [creatorCount, setCreatorCount] = useState(0);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(
    moment().add(-7, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  const [totalEarning, setTotalEarning] = useState(0);
  const [totalComissions, setTotalComissions] = useState(0);
  const [totalConvenienceFee, setTotalConvenienceFee] = useState(0);
  // const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showLifetimeEarnings, setShowLifetimeEarnings] = useState(false);
  const [natureFilter, setNatureFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());
  const [presentlyViewing, setPresentlyViewing] = useState("");
  const [whitelabelPlanTypeFilter, setWhitelabelPlanTypeFilter] =
    useState("all");

  // Account Manager related states
  const [accountManagerCSVModalVisible, setAccountManagerCSVModalVisible] =
    useState(false);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvDownloadLoading, setCsvDownloadLoading] = useState(false);
  const [cohortDownloadLoading, setCohortDownloadLoading] = useState(false);
  const [ledgerDownloadLoading, setLedgerDownloadLoading] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  // const [accountManagers, setAccountManagers] = useState([]);
  const [selectedAccountManager, setSelectedAccountManager] = useState(null);

  const controlsElementsStyle = {
    display: "flex",
    width: "100%",
    marginBottom: "8px",
  };

  const errorHandle = (error) => {
    if (error.status === 401 || error.code === 401) {
      localStorage.clear();
      history.push("/");
      return error?.message || error?.result
        ? message.error(error?.message || error?.result)
        : message.error("Something went wrong");
    }
    console.log(error);
    return error?.message || error?.result
      ? message.error(error?.message || error?.result)
      : message.error("Something went wrong");
  };

  const downloadCSV = async () => {
    setCsvDownloadLoading(true);
    try {
      const resp = await getCreatorsEarnings(
        startDate.toISOString(),
        endDate.toISOString(),
        debouncedSearch,
        natureFilter,
        typeFilter,
        whitelabelPlanTypeFilter,
        true,
        page,
        selectedAccountManager,
        showLifetimeEarnings
      );
      fileDownload(
        resp,
        `${startDate.toISOString()}-${endDate.toISOString()}-${
          search !== "" ? search : ""
        }-creators.csv`
      );
    } catch (error) {
      message.error("Failed to download CSV. Please try again.");
    } finally {
      setCsvDownloadLoading(false);
    }
  };

  const downloadCohortCSV = async () => {
    setCohortDownloadLoading(true);
    try {
      // Add a delay to ensure loader is visible
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const resp = await getCohortSheet();
      fileDownload(resp, `cohort-sheet.csv`);
    } catch (error) {
      message.error("Failed to download Cohort Sheet. Please try again.");
    } finally {
      setCohortDownloadLoading(false);
    }
  };

  const downloadLedgerCSV = async (currency) => {
    setLedgerDownloadLoading(true);
    try {
      const resp = await getCreatorWalletStatement(
        startDate.toISOString(),
        endDate.toISOString(),
        currency
      );
      fileDownload(
        resp,
        `creator-wallet-statement-${startDate.toISOString()}-${endDate.toISOString()}.csv`
      );
    } catch (error) {
      message.error("Failed to download Ledger. Please try again.");
    } finally {
      setLedgerDownloadLoading(false);
    }
  };

  useEffect(async () => {
    setLoading(true);
    try {
      const resp = await getCreatorsEarnings(
        startDate.toISOString(),
        endDate.toISOString(),
        debouncedSearch,
        natureFilter,
        typeFilter,
        whitelabelPlanTypeFilter,
        false,
        page,
        selectedAccountManager,
        showLifetimeEarnings
      );
      if (resp?.code === 0) {
        setCreators(resp.result.creators);
        setCreatorCount(resp.result.creatorCount);
        setTotalPages(Math.ceil(resp.result.creatorCount / 100));
        setTotalEarning(resp.result?.totalEarning);
        setTotalComissions(resp.result?.totalComissions);
        setTotalConvenienceFee(resp.result?.totalConvenienceFee);
        // setTotalOrders(resp.result.totalOrders);
        setLoading(false);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
        setLoading(false);
      }
    } catch (err) {
      errorHandle(err);
    }
  }, [
    page,
    debouncedSearch,
    startDate,
    endDate,
    natureFilter,
    typeFilter,
    whitelabelPlanTypeFilter,
    selectedAccountManager,
  ]);

  useEffect(() => {
    if (showLifetimeEarnings) {
      setStartDate(moment("2020-12-28 14:30:14.755Z").startOf("day"));
    } else {
      setStartDate(moment().add(-7, "days").startOf("day"));
    }
  }, [showLifetimeEarnings]);

  const creatorsTable = () => {
    const columnData = [
      {
        title: "Creator",
        dataIndex: "_id",
        key: "_id",
        render: (text, record) => (
          <Space size="small">
            <Avatar src={record?.profilePicUrl} size={32} />
            <Typography
              style={{ fontWeight: 500, fontSize: 14, color: "#1890ff" }}
            >
              {record?.name} {record?.isWhitelabelled ? "(Whitelabelled)" : ""}
            </Typography>
          </Space>
        ),
      },
      {
        title: "Account Manager",
        dataIndex: "accountManagerName",
        key: "accountManagerName",
        render: (_, record) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {record?.accountManagerName || "-"}
            <br />
            {record?.accountManagerEmail || "-"}
          </span>
        ),
      },
      {
        title: "Mangoes",
        dataIndex: "nMangos",
        key: "nMangos",
      },
      {
        title: "Earnings",
        dataIndex: "earning",
        key: "earning",
        render: (text) => <Typography>{text?.toFixed(2)}</Typography>,
      },
      {
        title: "GST",
        dataIndex: "gst",
        key: "gst",
        render: (text) => <Typography>{text?.toFixed(2)}</Typography>,
      },
      {
        title: "Comissions",
        dataIndex: "comissions",
        key: "comissions",
        render: (text) => <Typography>{text?.toFixed(2)}</Typography>,
      },
      {
        title: "Convenience Fee",
        dataIndex: "convenienceFee",
        key: "convenienceFee",
        render: (text) => <Typography>{text?.toFixed(2)}</Typography>,
      },
      {
        title: "Orders",
        dataIndex: "totalOrders",
        key: "totalOrders",
      },
    ];

    const creatorNatureData = {
      title: "Creator Nature",
      dataIndex: "creatorNature",
      key: "creatorNature",
      render: (text, record) => (
        <CreatorNatureAction
          userId={record?._id}
          presentNature={record?.creatorNature || "unassigned"}
          onNatureChange={(nature) => {
            setCreators((prevState) =>
              prevState.map((creator) => {
                if (creator._id === record._id) {
                  return { ...creator, creatorNature: nature };
                }
                return creator;
              })
            );
          }}
        />
      ),
    };

    return (
      <>
        <div
          style={{
            display: "grid",
          }}
        >
          {loading ? (
            <LoadingOutlined
              style={{
                fontSize: "50px",
                alignSelf: "center",
                justifySelf: "center",
              }}
            />
          ) : (
            <div>
              <Table
                key={tableKey}
                columns={[...columnData, creatorNatureData]}
                dataSource={creators}
                pagination={{ pageSize: 100, hideOnSinglePage: true }}
                rowKey={(record) => record._id}
                expandable={{
                  expandedRowRender: (record) => {
                    return (
                      <>
                        <Typography.Title level={5}>
                          Mango Breakdown
                        </Typography.Title>
                        <MangoExpandTable
                          creatorId={record._id}
                          startDate={startDate.toISOString()}
                          endDate={endDate.toISOString()}
                        />
                        <Typography.Title
                          level={5}
                          style={{ marginTop: "20px" }}
                        >
                          Wallet Ledger
                        </Typography.Title>
                        <WalletLedgerTable creatorId={record?._id} />
                      </>
                    );
                  },
                  expandedRowKeys: [presentlyViewing],
                  onExpand: (expanded, record) => {
                    if (expanded) {
                      setPresentlyViewing(record?._id);
                    } else {
                      setPresentlyViewing("");
                    }
                  },
                }}
              />
              <Pagination
                current={page}
                pageSize={100}
                pageSizeOptions={["100"]}
                total={totalPages * 100}
                onChange={(value) => {
                  setPage(value);
                }}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  // Account Manager CSV functionality
  const handleCSVUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n");
      const headers = lines[0]
        .split(",")
        .map((h) => h.trim().replace(/"/g, ""));

      // Validate headers
      if (
        headers.length !== 2 ||
        !headers.includes("host") ||
        !headers.includes("accountManagerEmail")
      ) {
        message.error(
          "CSV must contain exactly two columns: host and accountManagerEmail"
        );
        return;
      }

      const data = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i]
            .split(",")
            .map((v) => v.trim().replace(/"/g, ""));
          if (values.length === 2) {
            data.push({
              host: values[0],
              accountManagerEmail: values[1],
            });
          }
        }
      }

      setCsvData(data);
      message.success(`CSV parsed successfully. ${data.length} entries found.`);
    };
    reader.readAsText(file);
    return false; // Prevent default upload behavior
  };

  const handleCSVValidation = async () => {
    if (!csvData || csvData.length === 0) {
      message.error("Please upload a CSV file first");
      return;
    }

    setCsvUploadLoading(true);
    try {
      const resp = await validateBulkAccountManager({ bulkEntries: csvData });

      if (resp.code === 201 || resp.code === 0) {
        setValidationResult(resp.result);

        if (resp.result.errorData && resp.result.errorData.length > 0) {
          message.error(
            "Some entries have errors. Please review and fix them."
          );
        } else if (
          resp.result.errorMessage &&
          resp.result.errorMessage.trim() !== ""
        ) {
          message.error(resp.result.errorMessage);
        } else if (
          resp.result.uploadableData &&
          resp.result.uploadableData.length > 0
        ) {
          message.success("Validation successful! Proceeding with upload...");
          // Proceed with final upload
          await handleFinalUpload(resp.result.uploadableData);
        } else {
          message.error("No valid data found for upload");
        }
      } else {
        message.error(resp?.message || "Validation failed");
      }
    } catch (err) {
      console.log(err);
      message.error("Validation failed. Please try again.");
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const handleFinalUpload = async (uploadableData) => {
    if (!uploadableData || uploadableData.length === 0) {
      message.error("No valid data to upload");
      return;
    }

    setCsvUploadLoading(true);
    try {
      const finalData = uploadableData.map((item) => ({
        creatorId: item.creatorId,
        accountManagerId: item.accountManagerId,
      }));

      const resp = await uploadBulkAccountManager({ bulkEntries: finalData });

      if (resp.code === 201 || resp.code === 0) {
        message.success("Account managers updated successfully!");
        setAccountManagerCSVModalVisible(false);
        setCsvData(null);
        setValidationResult(null);
        // Refresh the table data
        window.location.reload();
      } else {
        message.error(resp?.message || "Upload failed");
      }
    } catch (err) {
      console.log(err);
      message.error("Upload failed. Please try again.");
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      {
        host: "example1.tagmango.com",
        accountManagerEmail: "manager1@tagmango.com",
      },
      {
        host: "example2.tagmango.com",
        accountManagerEmail: "manager2@tagmango.com",
      },
    ];

    const csvContent = `host,accountManagerEmail\n${sampleData
      .map((row) => `${row.host},${row.accountManagerEmail}`)
      .join("\n")}`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "account-manager-sample.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadErrorCSV = () => {
    if (
      !validationResult?.errorData ||
      validationResult.errorData.length === 0
    ) {
      message.error("No error data to download");
      return;
    }

    const csvContent = `host,accountManagerEmail,errorMessage\n${validationResult.errorData
      .map(
        (row) =>
          `${row.host},${row.accountManagerEmail},${row.errorMessage || ""}`
      )
      .join("\n")}`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "account-manager-errors.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetCSVModal = () => {
    setCsvData(null);
    setValidationResult(null);
    setCsvUploadLoading(false);
  };

  useEffect(() => {
    dispatch(fetchManagers());
  }, []);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <Col span={4}>
          <Card title="Total Transaction Value" bordered={false}>
            <Typography>₹ {totalEarning}</Typography>
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Comissions" bordered={false}>
            <Typography>₹ {totalComissions}</Typography>
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Convenience Fee" bordered={false}>
            <Typography>₹ {totalConvenienceFee}</Typography>
          </Card>
        </Col>
        <Col span={4}>
          <Card title="Total Creators" bordered={false}>
            <Typography>{creatorCount}</Typography>
          </Card>
        </Col>
        <Col
          span={8}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            flexDirection: "column",
          }}
        >
          <div style={controlsElementsStyle}>
            <Input
              minLength={2}
              value={search}
              className="searchbar"
              style={{ width: "100%", marginBottom: "4px" }}
              placeholder="Search"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div
            style={{
              display: "flex",
              width: "100%",
              marginBottom: "8px",
              justifyContent: "center",
            }}
          >
            <DatePicker.RangePicker
              disabled={showLifetimeEarnings}
              onChange={(date) => {
                if (date) {
                  setStartDate(date[0].startOf("day"));
                  setEndDate(date[1].endOf("day"));
                } else {
                  setStartDate(moment().add(-7, "days").startOf("day"));
                  setEndDate(moment().endOf("day"));
                }
              }}
              value={[startDate, endDate]}
            />
          </div>
          <div
            style={{
              ...controlsElementsStyle,
              alignItems: "center",
            }}
          >
            <Checkbox
              style={{ width: "50%" }}
              onChange={() => {
                setShowLifetimeEarnings(!showLifetimeEarnings);
              }}
            >
              Show Lifetime Earnings
            </Checkbox>
            <Select
              style={{ width: "50%" }}
              placeholder="Creator Nature Filter"
              allowClear
              onChange={setNatureFilter}
              mode="multiple"
            >
              <Select.Option value="organic">Organic</Select.Option>
              <Select.Option value="growth">Growth</Select.Option>
              <Select.Option value="retention">Retention</Select.Option>
              <Select.Option value="mia">MIA</Select.Option>
            </Select>
          </div>
          <div
            style={{
              ...controlsElementsStyle,
              justifyContent: "center",
            }}
          >
            <Select
              style={{ width: "50%" }}
              placeholder="Creator Type Filter"
              allowClear
              onChange={setTypeFilter}
              value={typeFilter}
            >
              <Select.Option value="all">All</Select.Option>
              <Select.Option value="whitelabelled">Whitelabel</Select.Option>
              <Select.Option value="tagmango">TagMango</Select.Option>
            </Select>
            <Select
              style={{ width: "50%" }}
              placeholder="Whitelabel Type Filter"
              allowClear
              onChange={setWhitelabelPlanTypeFilter}
              value={whitelabelPlanTypeFilter}
            >
              <Select.Option value="all">All</Select.Option>
              <Select.Option value="freedom-plan">Freedom Plan</Select.Option>
              <Select.Option value="enterprise-plan">
                Enterprise Plan
              </Select.Option>
            </Select>
          </div>
          <div
            style={{
              ...controlsElementsStyle,
              justifyContent: "center",
            }}
          >
            <Select
              style={{ width: "100%" }}
              placeholder="Select account manager"
              allowClear
              value={selectedAccountManager}
              onSelect={setSelectedAccountManager}
              onClear={() => setSelectedAccountManager(null)}
            >
              {managers.map((manager) => (
                <Select.Option key={manager._id} value={manager._id}>
                  {manager.name} ({manager.email})
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>
        <div
          style={{
            ...controlsElementsStyle,
            justifyContent: "center",
            flexWrap: "wrap",
            rowGap: "8px",
          }}
        >
          <Button
            type="primary"
            onClick={downloadCSV}
            loading={csvDownloadLoading}
            style={{ marginRight: "4px" }}
          >
            Download CSV
          </Button>
          <Button
            type="primary"
            onClick={downloadCohortCSV}
            loading={cohortDownloadLoading}
            style={{ marginRight: "4px" }}
          >
            Download Cohort Sheet
          </Button>
          <Button
            type="primary"
            onClick={() => downloadLedgerCSV("INR")}
            loading={ledgerDownloadLoading}
            style={{ marginRight: "4px" }}
          >
            Download Ledger Of Given Range in INR
          </Button>

          <Button
            type="primary"
            onClick={() => downloadLedgerCSV("USD")}
            loading={ledgerDownloadLoading}
            style={{ marginRight: "4px" }}
          >
            Download Ledger Of Given Range in USD
          </Button>

          <Button
            className="siteBtn accentBtnV1"
            onClick={() => setAccountManagerCSVModalVisible(true)}
            style={{ marginRight: "4px" }}
          >
            <UploadOutlined />
            Upload Account Manager CSV
          </Button>

          <RefreshEarningsButton />
        </div>
      </Row>
      {creatorsTable()}

      {/* CSV Upload Modal */}
      <Modal
        visible={accountManagerCSVModalVisible}
        onCancel={() => {
          setAccountManagerCSVModalVisible(false);
          resetCSVModal();
        }}
        footer={null}
        width={800}
        destroyOnClose
        title="Upload Account Manager CSV"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <Typography.Title level={5}>Instructions:</Typography.Title>
            <Typography.Text>
              Upload a CSV file with exactly two columns: <strong>host</strong>{" "}
              and <strong>accountManagerEmail</strong>
            </Typography.Text>
            <br />
            <Button
              type="link"
              onClick={downloadSampleCSV}
              icon={<DownloadOutlined />}
            >
              Download Sample CSV
            </Button>
          </div>

          <div>
            <Typography.Title level={5}>Upload CSV:</Typography.Title>
            <Upload
              accept=".csv"
              beforeUpload={handleCSVUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={csvUploadLoading}>
                Choose CSV File
              </Button>
            </Upload>
            {csvData && (
              <Typography.Text style={{ marginLeft: 16 }}>
                ✓ {csvData.length} entries loaded
              </Typography.Text>
            )}
          </div>

          {csvData && csvData.length > 0 && (
            <div>
              <Button
                type="primary"
                onClick={handleCSVValidation}
                loading={csvUploadLoading}
                disabled={!csvData || csvData.length === 0}
              >
                Validate & Upload
              </Button>
            </div>
          )}

          {validationResult && (
            <div>
              <Typography.Title level={5}>Validation Results:</Typography.Title>

              {validationResult.errorData &&
                validationResult.errorData.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="danger">
                      {validationResult.errorData.length} entries have errors
                    </Typography.Text>
                    <Button
                      type="link"
                      onClick={downloadErrorCSV}
                      icon={<DownloadOutlined />}
                      danger
                    >
                      Download Error Entries
                    </Button>
                  </div>
                )}

              {validationResult.uploadableData &&
                validationResult.uploadableData.length > 0 && (
                  <div>
                    <Typography.Text type="success">
                      {validationResult.uploadableData.length} entries are valid
                      and ready for upload
                    </Typography.Text>
                  </div>
                )}

              {validationResult.errorMessage && (
                <Typography.Text type="danger">
                  {validationResult.errorMessage}
                </Typography.Text>
              )}
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default CreatorEarnings;
